import {
  Component,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import * as fromLayout from '../layout';
import { NEW_CHAT_TITLE_DEFAULT } from './chats.constants';
import { ChatsDesktopResponsive } from './chats.desktop.responsive';
import { ChatsHelper } from './chats.helper';
import { ChatsMobileResponsive } from './chats.mobile.responsive';
import { CHAT_TITLE_PATTERN } from './components/navigation/navigation-chats-list.helper';
import { RouterService } from './shared/services/router.service';
import {
  ChatModalActions,
  ChatsActions,
  ChatsState,
  chatsFeature,
} from './store';

@Component({
  selector: 'squadbox-chats',
  imports: [
    TranslateModule,
    FormsModule,
    RouterModule,
    ChatsMobileResponsive,
    ChatsDesktopResponsive,
    fromSeriousSystem.ModalComponent,
    fromSeriousSystem.InputComponent,
  ],
  providers: [ChatsHelper],
  template: `
    @if (isMobile()) {
      <squadbox-chats-mobile
        [chatNavigationItems]="chatNavigationItems()"
        [isNewChat]="isNewChat()"
      >
      </squadbox-chats-mobile>
    } @else {
      <squadbox-chats-desktop
        [chatNavigationItems]="chatNavigationItems()"
        [isNewChat]="isNewChat()"
        (chatDeleteClicked)="onDeleteChat($event)"
      >
      </squadbox-chats-desktop>
    }
    <sd-modal
      [isModalVisible]="isDeleteChatModalOpened()"
      [modalOptions]="{
        title: translateService.instant('CHAT.DELETE_CHAT_MODAL.TITLE'),
        actions: [
          {
            label: translateService.instant(
              'CHAT.DELETE_CHAT_MODAL.CTA_CANCEL'
            ),
            action: 'cancel',
            type: 'cancel',
            disabled: false,
          },
          {
            label: translateService.instant(
              'CHAT.DELETE_CHAT_MODAL.CTA_DELETE'
            ),
            action: 'delete',
            type: 'delete',
            disabled: false,
          },
        ],
        backdropDismiss: true,
      }"
      (triggerAction)="onTriggerAction($event)"
    >
      <div
        class="w-auto tablet-portrait:w-120"
        innerHTML="{{
          'CHAT.DELETE_CHAT_MODAL.DESCRIPTION'
            | translate: { chatName: chatNameForDeleteModal() }
        }}"
      ></div>
    </sd-modal>
    <sd-modal
      [isModalVisible]="isRenameChatModalOpened()"
      [modalOptions]="{
        title: translateService.instant('CHAT.RENAME_CHAT_MODAL.TITLE'),
        actions: [
          {
            label: translateService.instant(
              'CHAT.RENAME_CHAT_MODAL.CTA_CANCEL'
            ),
            action: 'cancel',
            type: 'cancel',
            disabled: false,
          },
          {
            label: translateService.instant(
              'CHAT.RENAME_CHAT_MODAL.CTA_RENAME'
            ),
            action: 'rename',
            type: 'save',
            disabled: isRenameSubmitDisabled(),
          },
        ],
        backdropDismiss: true,
        dragDismiss: true,
        maxWidth: 358,
        footerClass: 'pt-0',
      }"
      (triggerAction)="onRenameAction($event)"
    >
      <sd-input
        #renameChatInput
        class="block w-full [&_input]:h-14"
        [accessibleLabel]="
          translateService.instant('CHAT.RENAME_CHAT_MODAL.PLACEHOLDER')
        "
        [label]="''"
        [placeholder]="
          translateService.instant('CHAT.RENAME_CHAT_MODAL.PLACEHOLDER')
        "
        [limit]="100"
        [ngModel]="renameValue()"
        (ngModelChange)="renameValue.set($event)"
      />
    </sd-modal>
  `,
  styles: [
    `
      @reference '../../styles.css';

      .main-navigation-item {
        @apply grid grid-cols-[30px_auto] items-center align-middle;
        @apply items-center overflow-hidden rounded tablet-landscape:px-6 px-4;
        @apply cursor-pointer hover:bg-primary-500/5;
        @apply [&>svg]:size-5 [&>div]:mr-2;
        @apply [&>div]:size-5 [&>div>img]:size-5;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ChatsPage {
  private readonly layoutStore = inject<Store<fromLayout.LayoutState>>(Store);
  private readonly chatsStore = inject<Store<ChatsState>>(Store);

  private readonly routerService = inject(RouterService);
  private readonly chatsHelper = inject(ChatsHelper);

  public readonly translateService = inject(TranslateService);

  private readonly chatToDeleteId = signal<number | null>(null);
  private readonly renamePreviousTitleRaw = signal(NEW_CHAT_TITLE_DEFAULT);
  private readonly renameChatInput =
    viewChild<fromSeriousSystem.InputComponent>('renameChatInput');
  private renameModalSeeded = false;

  public readonly selectedChatId = computed(() => {
    const selectedChat = this.chatsStore.selectSignal(
      chatsFeature.selectSelectedChat,
    )();
    return selectedChat?.id ?? null;
  });
  public readonly selectedChatTitle = computed(() => {
    const title = this.chatsStore.selectSignal(
      chatsFeature.selectSelectedChat,
    )()?.title;

    return this.chatsHelper.getChatTitle(title);
  });
  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  public readonly isDeleteChatModalOpened = this.chatsStore.selectSignal(
    chatsFeature.selectIsDeleteChatModalOpened,
  );
  public readonly isRenameChatModalOpened = this.chatsStore.selectSignal(
    chatsFeature.selectIsRenameChatModalOpened,
  );
  public readonly selectedChat = this.chatsStore.selectSignal(
    chatsFeature.selectSelectedChat,
  );
  public readonly isNewChat = computed(() => {
    return this.selectedChat()?.messages?.length === 0;
  });
  public readonly chatNavigationItems = computed(() => {
    const items = this.chatsStore.selectSignal(
      chatsFeature.selectChatNavigationItems,
      {},
    );

    return items();
  });
  public readonly chatTitleToDelete = computed(() => {
    if (this.chatToDeleteId() !== null) {
      const chatToDelete = this.chatNavigationItems().find(
        (item) => item.id === this.chatToDeleteId(),
      );
      if (chatToDelete) {
        return this.chatsHelper.getChatTitle(chatToDelete.title);
      }
    }
    return this.selectedChatTitle();
  });
  public readonly chatNameForDeleteModal = computed(() =>
    escapeHtml(this.chatTitleToDelete()),
  );
  public readonly renameValue = signal('');
  public readonly isRenameSubmitDisabled = computed(() => {
    const nextTitle = this.renameValue().trim();
    return (
      nextTitle.length === 0 ||
      nextTitle.length > 100 ||
      // Mirror the API DTO's single-line @Matches and
      // @NotEquals(NEW_CHAT_TITLE_DEFAULT) so the commit never 400s and
      // reverts the optimistic update (pasted U+2028/U+2029/NUL survive a
      // single-line input; \r\n do not).
      !CHAT_TITLE_PATTERN.test(nextTitle) ||
      nextTitle === NEW_CHAT_TITLE_DEFAULT ||
      nextTitle === this.chatsHelper.getChatTitle(this.renamePreviousTitleRaw())
    );
  });

  constructor() {
    effect(() => {
      if (!this.isRenameChatModalOpened()) {
        this.renameModalSeeded = false;
        return;
      }
      if (this.renameModalSeeded) {
        return;
      }

      this.renameModalSeeded = true;
      untracked(() => {
        const chat = this.selectedChat();
        this.renamePreviousTitleRaw.set(chat?.title ?? NEW_CHAT_TITLE_DEFAULT);
        this.renameValue.set(this.chatsHelper.getChatTitle(chat?.title));
        queueMicrotask(() => this.renameChatInput()?.focus());
      });
    });
  }

  public async onTriggerAction(action: string) {
    switch (action) {
      case 'delete': {
        /**
         * Priority 1: If the chatToDeleteId is set, delete the chat (this is the case when deleting from navigation dropdown menu)
         * Priority 2: Otherwise, delete the selected chat (this is the case when deleting from mobile topbar)
         */
        const chatIdToDelete = this.chatToDeleteId() ?? this.selectedChatId();
        if (chatIdToDelete === this.selectedChatId()) {
          await this.routerService.navigateToEmptyConversation();
        }
        if (chatIdToDelete !== null) {
          this.chatsStore.dispatch(
            ChatsActions.deleteChat({ id: chatIdToDelete }),
          );
          this.chatToDeleteId.set(null);
        }
        this.chatsStore.dispatch(ChatModalActions.closeDeleteChatModal());
        break;
      }
      case 'cancel':
        this.chatToDeleteId.set(null);
        this.chatsStore.dispatch(ChatModalActions.closeDeleteChatModal());
        break;
    }
  }

  public onDeleteChat(chatId: number) {
    this.chatToDeleteId.set(chatId);
    this.chatsStore.dispatch(ChatModalActions.openDeleteChatModal());
  }

  public onRenameAction(action: string) {
    if (action === 'rename' && !this.isRenameSubmitDisabled()) {
      const chatId = this.selectedChatId();
      if (chatId !== null) {
        this.chatsStore.dispatch(
          ChatsActions.renameChat({
            id: chatId,
            title: this.renameValue().trim(),
            previousTitle: this.renamePreviousTitleRaw(),
            requestId: crypto.randomUUID(),
            renamedAt: new Date().toISOString(),
            source: 'mobile-modal',
          }),
        );
      }
    }

    this.chatsStore.dispatch(ChatModalActions.closeRenameChatModal());
    queueMicrotask(() => {
      document
        .querySelector<HTMLButtonElement>('[data-chat-more-options-trigger]')
        ?.focus();
    });
  }
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
